import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleDataMap, type ArticleData } from '@/mocks/articles';

declare const __VITE_SITE_URL__: string;

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const article: ArticleData | undefined = slug ? articleDataMap[slug] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center px-4">
          <h1 className="text-3xl font-heading font-bold text-foreground-900 mb-4">Artykuł nie znaleziony</h1>
          <p className="text-foreground-500 mb-6">Przepraszamy, ale szukany artykuł nie istnieje.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-white text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-home-4-line"></i>
            </span>
            Strona główna
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const siteUrl = typeof __VITE_SITE_URL__ !== 'undefined' ? __VITE_SITE_URL__ : 'https://kakobuy.click';
  const articleUrl = `${siteUrl}/artykul/${slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Strona główna',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Artykuły',
        item: `${siteUrl}/#artykuly`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: articleUrl,
      },
    ],
  };

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <article>
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-8 lg:px-12 pt-20 md:pt-24 pb-2">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
            <Link to="/" className="hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap">Strona główna</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <Link to="/#artykuly" className="hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap">Artykuły</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-600 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>
        </div>

        {/* Hero header */}
        <header className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">{article.category}</span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime} czytania</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {article.description}
            </p>
          </div>
        </header>

        {/* Featured image */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-10">
          <div className="max-w-4xl mx-auto">
            <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden">
              <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-4 md:py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
              {/* Sidebar TOC */}
              <aside className="lg:w-56 shrink-0">
                <div className="sticky top-24 p-5 rounded-xl bg-background-100 border border-background-200/50">
                  <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">Spis treści</h4>
                  <nav className="flex flex-col gap-2">
                    {article.sections.map((section, i) => (
                      <a
                        key={i}
                        href={`#section-${i + 1}`}
                        className="text-xs text-foreground-500 hover:text-accent-600 transition-colors leading-relaxed cursor-pointer"
                      >
                        {section.heading}
                      </a>
                    ))}
                    {article.faq.length > 0 && (
                      <a
                        href="#faq"
                        className="text-xs text-foreground-500 hover:text-accent-600 transition-colors leading-relaxed cursor-pointer"
                      >
                        Najczęściej zadawane pytania
                      </a>
                    )}
                  </nav>
                </div>
              </aside>

              {/* Main content */}
              <div className="flex-1 min-w-0">
                {article.sections.map((section, i) => (
                  <section key={i} id={`section-${i + 1}`} className="mb-12">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-5">
                      {section.heading}
                    </h2>
                    <div className="space-y-4">
                      {section.paragraphs.map((p, j) => (
                        <p key={j} className="text-sm md:text-base text-foreground-600 leading-relaxed">
                          {p}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}

                {/* FAQ */}
                {article.faq.length > 0 && (
                  <section id="faq" className="mb-12">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-6">
                      Najczęściej zadawane pytania (FAQ)
                    </h2>
                    <div className="space-y-3">
                      {article.faq.map((faq, idx) => (
                        <div key={idx} className="rounded-xl bg-background-100 border border-background-200/50 overflow-hidden">
                          <button
                            onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                            className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                          >
                            <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                            <span className={`w-5 h-5 flex items-center justify-center shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                              <i className="ri-arrow-down-s-line text-foreground-400"></i>
                            </span>
                          </button>
                          {openFaq === idx && (
                            <div className="px-5 pb-4">
                              <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Back to home CTA */}
                <div className="mt-10 p-6 rounded-2xl bg-accent-50 border border-accent-200/50 text-center">
                  <p className="text-sm text-foreground-600 mb-3">
                    Wróć do strony głównej, aby przeglądać wszystkie kategorie produktów i więcej artykułów
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-accent-500 text-white text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-home-4-line"></i>
                    </span>
                    Strona główna
                  </Link>
                </div>

                {/* CTA to kaspreadsheet */}
                <div className="mt-8 p-6 md:p-8 rounded-2xl bg-background-900 text-center">
                  <h3 className="text-xl md:text-2xl font-heading font-semibold text-white mb-3">
                    Gotowy, aby zacząć zakupy?
                  </h3>
                  <p className="text-sm text-foreground-400 mb-5 max-w-md mx-auto">
                    Otwórz Kakobuy Spreadsheet i odkryj tysiące sprawdzonych produktów w najlepszych cenach.
                  </p>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-accent-500 text-white text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
                  >
                    Kakobuy Spreadsheet
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* FAQ Schema */}
      {article.faq.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: article.faq.map((faq) => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.a,
                },
              })),
            }),
          }}
        />
      )}

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.description,
            image: article.image,
            url: articleUrl,
            datePublished: article.date,
            dateModified: article.date,
            author: {
              '@type': 'Organization',
              name: 'Kakobuy Spreadsheet Polska',
              url: siteUrl,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Kakobuy Spreadsheet Polska',
              url: siteUrl,
            },
            inLanguage: 'pl-PL',
            isPartOf: {
              '@type': 'WebSite',
              name: 'Kakobuy Spreadsheet Polska',
              url: siteUrl,
            },
          }),
        }}
      />

      <Footer />
    </main>
  );
}